import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Search, ChevronDown, X, Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTheme } from '@/components/theme-provider';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { format } from 'date-fns';

import slackColorIcon from '../assets/icons/slack-color.png';
import argocdIcon from '../assets/icons/argocd.svg';
import fluxcdIcon from '../assets/icons/flux-cd.svg';
import karpenterIcon from '../assets/icons/karpenter.svg';
import kyvernoIcon from '../assets/icons/kyverno.svg';
import trivyIcon from '../assets/icons/trivy.svg';
import confluenceIcon from '../assets/icons/confluence.svg';
import jiraIcon from '../assets/icons/jira.svg';
import linearIcon from '../assets/icons/linear.png';
import linearDarkIcon from '../assets/icons/linear-dark-mode.png';
import datadogLightIcon from '../assets/icons/datadog-light-mode.svg';
import datadogDarkIcon from '../assets/icons/datadog-dark-mode.png';
import gitlabIcon from '../assets/icons/gitlab-clean.svg';
import kestrelLightLogo from '../assets/icons/kestrel-light.svg';
import kestrelDarkLogo from '../assets/icons/kestrel-dark.svg';
import awsLightLogo from '../assets/icons/aws.svg';
import awsDarkLogo from '../assets/icons/aws-dark.svg';
import posthogIcon from '../assets/icons/posthog-logo-stacked.svg';
import posthogLightIcon from '../assets/icons/posthog-light.png';
import vercelIcon from '../assets/icons/vercel.svg';
import vercelLightIcon from '../assets/icons/vercel-light.svg';
import railwayIcon from '../assets/icons/railway.svg';
import flyioIcon from '../assets/icons/flyio.svg';
import nebiusIcon from '../assets/icons/NBIS.svg';
import { ReactComponent as BeamIcon } from '../assets/icons/beam.svg';
import { ReactComponent as DaytonaIcon } from '../assets/icons/daytona.svg';
import supabaseIcon from '../assets/icons/supabase-logo-icon.svg';
import { ReactComponent as PlanetScaleIcon } from '../assets/icons/planetscale-mono.svg';
import neonIcon from '../assets/icons/neon-logomark-light-color.svg';
import clickhouseIcon from '../assets/icons/clickhouse.svg';
import jenkinsIcon from '../assets/icons/Jenkins.svg';
import { ReactComponent as CircleCIIcon } from '../assets/icons/circleci-mono.svg';
import { ReactComponent as SonarCloudIcon } from '../assets/icons/sonarcloud-mono.svg';
import { ReactComponent as OktaIcon } from '../assets/icons/okta-mono.svg';
import databricksIcon from '../assets/icons/databricks.png';
import { ReactComponent as GCPFilterIcon } from '../assets/icons/gcp.svg';
import terraformIcon from '../assets/icons/HashiCorp Terraform.svg';
import pulumiIcon from '../assets/icons/pulumi-logo-mark-color.svg';
import vaultIcon from '../assets/icons/hashicorp-vault.svg';
import infisicalIcon from '../assets/icons/infisical.svg';
import pagerdutyColorIcon from '../assets/icons/pagerduty-icon.svg';
import cloudflareIcon from '../assets/icons/cloudflare.svg';
import helmLightIcon from '../assets/icons/helm-light.svg';
import helmDarkIcon from '../assets/icons/helm-dark.svg';
import { ShipWheel } from 'lucide-react';

export interface WorkflowFilterValues {
  name: string;
  integrations: string[];
  createdBy: string[];
  statuses: string[];
  lastUpdated: string; // ISO date range: "from,to" or empty
}

export interface CreatedByOption {
  id: string;
  email: string;
  pictureUrl?: string;
}

interface WorkflowFiltersProps {
  filters: WorkflowFilterValues;
  onChange: (filters: WorkflowFilterValues) => void;
  createdByOptions: CreatedByOption[];
}

const ALL_INTEGRATIONS = [
  { value: 'kubernetes', label: 'Kubernetes' },
  { value: 'argocd', label: 'ArgoCD' },
  { value: 'argo-rollouts', label: 'Argo Rollouts' },
  { value: 'fluxcd', label: 'Flux CD' },
  { value: 'karpenter', label: 'Karpenter' },
  { value: 'kyverno', label: 'Kyverno' },
  { value: 'trivy', label: 'Trivy' },
  { value: 'helm', label: 'Helm' },
  { value: 'jenkins', label: 'Jenkins' },
  { value: 'circleci', label: 'CircleCI' },
  { value: 'sonarcloud', label: 'SonarCloud' },
  { value: 'okta', label: 'Okta' },
  { value: 'databricks', label: 'Databricks' },
  { value: 'gcp', label: 'Google Cloud' },
  { value: 'terraform', label: 'Terraform Cloud' },
  { value: 'pulumi', label: 'Pulumi Cloud' },
  { value: 'vault', label: 'HashiCorp Vault' },
  { value: 'infisical', label: 'Infisical' },
  { value: 'github', label: 'GitHub' },
  { value: 'gitlab', label: 'GitLab' },
  { value: 'slack', label: 'Slack' },
  { value: 'vercel', label: 'Vercel' },
  { value: 'railway', label: 'Railway' },
  { value: 'flyio', label: 'Fly.io' },
  { value: 'nebius', label: 'Nebius' },
  { value: 'beam', label: 'Beam' },
  { value: 'daytona', label: 'Daytona' },
  { value: 'cloudflare', label: 'Cloudflare' },
  { value: 'supabase', label: 'Supabase' },
  { value: 'planetscale', label: 'PlanetScale' },
  { value: 'neon', label: 'Neon' },
  { value: 'clickhouse', label: 'ClickHouse' },
  { value: 'aws', label: 'AWS' },
  { value: 'pagerduty', label: 'PagerDuty' },
  { value: 'datadog', label: 'Datadog' },
  { value: 'posthog', label: 'PostHog' },
  { value: 'confluence', label: 'Confluence' },
  { value: 'jira', label: 'Jira' },
  { value: 'linear', label: 'Linear' },
  { value: 'kestrel', label: 'Kestrel' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'draft', label: 'Draft' },
];

export function WorkflowFilters({ filters, onChange, createdByOptions }: WorkflowFiltersProps) {
  const update = (partial: Partial<WorkflowFilterValues>) => {
    onChange({ ...filters, ...partial });
  };

  // Debounced name input — local state + delayed propagation
  const [localName, setLocalName] = useState(filters.name);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (localName === filters.name) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      update({ name: localName });
    }, 800);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localName]);

  // Sync external changes back to local
  useEffect(() => { setLocalName(filters.name); }, [filters.name]);

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Name search (debounced) */}
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
        <Input
          value={localName}
          onChange={(e) => setLocalName(e.target.value)}
          placeholder="Filter by name..."
          className="h-8 w-44 pl-7 text-xs"
        />
      </div>

      {/* Integrations multi-select with icons */}
      <IntegrationsPopover
        selected={filters.integrations}
        onChange={(v) => update({ integrations: v })}
      />

      {/* Created by multi-select with profile pics */}
      <CreatedByPopover
        options={createdByOptions}
        selected={filters.createdBy}
        onChange={(v) => update({ createdBy: v })}
      />

      {/* Status multi-select */}
      <MultiSelectPopover
        label="Status"
        options={STATUS_OPTIONS}
        selected={filters.statuses}
        onChange={(v) => update({ statuses: v })}
      />

      {/* Date range picker */}
      <DateRangeFilter
        value={filters.lastUpdated}
        onChange={(v) => update({ lastUpdated: v })}
      />

      {/* Clear all */}
      {(filters.name || filters.integrations.length > 0 || filters.createdBy.length > 0 || filters.statuses.length > 0 || filters.lastUpdated) && (
        <button
          onClick={() => { onChange({ name: '', integrations: [], createdBy: [], statuses: [], lastUpdated: '' }); setLocalName(''); }}
          className="flex items-center gap-1 h-8 px-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-3 h-3" />
          Clear
        </button>
      )}
    </div>
  );
}

function IntegrationIcon({ integration, size = 14 }: { integration: string; size?: number }) {
  const { theme } = useTheme();
  const isDark =
    theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const s = { width: size, height: size };

  switch (integration) {
    case 'kubernetes': return <ShipWheel style={s} className="text-blue-500" />;
    case 'argocd': return <img src={argocdIcon} alt="" style={s} />;
    case 'argo-rollouts': return <img src={argocdIcon} alt="" style={s} />;
    case 'fluxcd': return <img src={fluxcdIcon} alt="" style={s} />;
    case 'karpenter': return <img src={karpenterIcon} alt="" style={s} />;
    case 'kyverno': return <img src={kyvernoIcon} alt="" style={s} />;
    case 'trivy': return <img src={trivyIcon} alt="" style={s} />;
    case 'helm': return <img src={isDark ? helmDarkIcon : helmLightIcon} alt="" style={s} className="object-contain" />;
    case 'cloudflare': return <img src={cloudflareIcon} alt="" style={s} className="object-contain" />;
    case 'github': return <img src={isDark ? 'https://github.githubassets.com/favicons/favicon-dark.svg' : 'https://github.githubassets.com/favicons/favicon.svg'} alt="" style={s} className="rounded-sm" />;
    case 'gitlab': return <img src={gitlabIcon} alt="" style={s} />;
    case 'slack': return <img src={slackColorIcon} alt="" style={s} />;
    case 'vercel': return <img src={isDark ? vercelLightIcon : vercelIcon} alt="" style={{ width: size * 0.85, height: size * 0.85 }} />;
    case 'railway': return <img src={railwayIcon} alt="" style={{ width: size * 0.85, height: size * 0.85, filter: isDark ? 'invert(1)' : 'none' }} className="object-contain" />;
    case 'flyio': return <img src={flyioIcon} alt="" style={{ width: size * 0.85, height: size * 0.85 }} className="object-contain" />;
    case 'nebius': return <img src={nebiusIcon} alt="" style={{ width: size * 0.85, height: size * 0.85 }} className="object-contain" />;
    case 'beam': return <BeamIcon style={{ width: size * 0.85, height: size * 0.85 }} className="text-foreground" />;
    case 'daytona': return <DaytonaIcon style={{ width: size * 0.85, height: size * 0.85 }} className="text-foreground" />;
    case 'supabase': return <img src={supabaseIcon} alt="Supabase" style={{ width: size * 0.85, height: size * 0.85 }} className="object-contain" />;
    case 'planetscale': return <PlanetScaleIcon style={{ width: size * 0.85, height: size * 0.85 }} className="text-foreground" />;
    case 'neon': return <img src={neonIcon} alt="Neon" style={{ width: size * 0.85, height: size * 0.85 }} className="object-contain" />;
    case 'clickhouse': return <img src={clickhouseIcon} alt="ClickHouse" style={{ width: size * 0.85, height: size * 0.85 }} className="object-contain" />;
    case 'jenkins': return <img src={jenkinsIcon} alt="Jenkins" style={{ width: size * 0.85, height: size * 0.85 }} className="object-contain" />;
    case 'circleci': return <CircleCIIcon style={{ width: size * 0.85, height: size * 0.85 }} className="text-foreground" />;
    case 'sonarcloud': return <SonarCloudIcon style={{ width: size * 0.85, height: size * 0.85 }} className="text-foreground" />;
    case 'okta': return <OktaIcon style={{ width: size * 0.85, height: size * 0.85 }} className="text-foreground" />;
    case 'databricks': return <img src={databricksIcon} alt="Databricks" style={{ width: size * 0.85, height: size * 0.85 }} className="object-contain" />;
    case 'gcp':
    case 'gcp-security':
    case 'gcp-data':
    case 'gcp-cost': return <GCPFilterIcon style={{ width: size * 0.85, height: size * 0.85 }} className="object-contain" />;
    case 'terraform': return <img src={terraformIcon} alt="Terraform" style={{ width: size * 0.85, height: size * 0.85 }} className="object-contain" />;
    case 'pulumi': return <img src={pulumiIcon} alt="Pulumi" style={{ width: size * 0.85, height: size * 0.85 }} className="object-contain" />;
    case 'vault': return <img src={vaultIcon} alt="Vault" style={{ width: size * 0.85, height: size * 0.85 }} className="object-contain" />;
    case 'infisical': return <img src={infisicalIcon} alt="Infisical" style={{ width: size * 0.85, height: size * 0.85 }} className="object-contain rounded-sm" />;
    case 'aws': return <img src={isDark ? awsDarkLogo : awsLightLogo} alt="" style={s} />;
    case 'pagerduty': return <img src={pagerdutyColorIcon} alt="" style={s} />;
    case 'datadog': return <img src={isDark ? datadogDarkIcon : datadogLightIcon} alt="" style={s} />;
    case 'posthog': return <img src={isDark ? posthogLightIcon : posthogIcon} alt="" style={s} />;
    case 'confluence': return <img src={confluenceIcon} alt="" style={s} />;
    case 'jira': return <img src={jiraIcon} alt="" style={s} />;
    case 'linear': return <img src={isDark ? linearDarkIcon : linearIcon} alt="" style={s} />;
    case 'kestrel': return <img src={isDark ? kestrelDarkLogo : kestrelLightLogo} alt="" style={s} />;
    default: return null;
  }
}

function IntegrationsPopover({ selected, onChange }: { selected: string[]; onChange: (v: string[]) => void }) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search) return ALL_INTEGRATIONS;
    const q = search.toLowerCase();
    return ALL_INTEGRATIONS.filter(o => o.label.toLowerCase().includes(q));
  }, [search]);

  const toggle = (value: string) => {
    onChange(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value]);
  };

  return (
    <Popover onOpenChange={(open) => { if (!open) setSearch(''); }}>
      <PopoverTrigger asChild>
        <button className={cn(
          "flex items-center gap-1.5 h-8 px-2.5 text-xs rounded-md border border-input bg-background hover:bg-accent/50 transition-colors",
          selected.length > 0 && "border-primary/40 bg-primary/5"
        )}>
          <span className="text-muted-foreground">Integrations</span>
          {selected.length > 0 && (
            <Badge variant="secondary" className="h-4 px-1 text-[10px] font-medium rounded">{selected.length}</Badge>
          )}
          <ChevronDown className="w-3 h-3 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-52 p-2">
        <div className="relative mb-2">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search integrations..."
            className="h-7 pl-6 text-xs"
            autoFocus
          />
        </div>
        <div className="max-h-48 overflow-y-auto space-y-0.5">
          {filtered.map(opt => (
            <label key={opt.value} className="flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-accent cursor-pointer text-xs">
              <Checkbox
                checked={selected.includes(opt.value)}
                onCheckedChange={() => toggle(opt.value)}
                className="h-3.5 w-3.5"
              />
              <IntegrationIcon integration={opt.value} size={14} />
              {opt.label}
            </label>
          ))}
          {filtered.length === 0 && <p className="text-xs text-muted-foreground px-2 py-1.5">No results</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function CreatedByPopover({ options, selected, onChange }: { options: CreatedByOption[]; selected: string[]; onChange: (v: string[]) => void }) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter(o => o.email.toLowerCase().includes(q));
  }, [options, search]);

  const toggle = (id: string) => {
    onChange(selected.includes(id) ? selected.filter(v => v !== id) : [...selected, id]);
  };

  return (
    <Popover onOpenChange={(open) => { if (!open) setSearch(''); }}>
      <PopoverTrigger asChild>
        <button className={cn(
          "flex items-center gap-1.5 h-8 px-2.5 text-xs rounded-md border border-input bg-background hover:bg-accent/50 transition-colors",
          selected.length > 0 && "border-primary/40 bg-primary/5"
        )}>
          <span className="text-muted-foreground">Created by</span>
          {selected.length > 0 && (
            <Badge variant="secondary" className="h-4 px-1 text-[10px] font-medium rounded">{selected.length}</Badge>
          )}
          <ChevronDown className="w-3 h-3 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2">
        <div className="relative mb-2">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search users..."
            className="h-7 pl-6 text-xs"
            autoFocus
          />
        </div>
        <div className="max-h-48 overflow-y-auto space-y-0.5">
          {filtered.map(opt => (
            <label key={opt.id} className="flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-accent cursor-pointer text-xs">
              <Checkbox
                checked={selected.includes(opt.id)}
                onCheckedChange={() => toggle(opt.id)}
                className="h-3.5 w-3.5"
              />
              {opt.pictureUrl ? (
                <img src={opt.pictureUrl} alt="" className="w-5 h-5 rounded-full object-cover" />
              ) : (
                <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-[9px] font-medium text-muted-foreground">
                  {opt.email[0]?.toUpperCase()}
                </div>
              )}
              <span className="truncate">{opt.email}</span>
            </label>
          ))}
          {filtered.length === 0 && <p className="text-xs text-muted-foreground px-2 py-1.5">No results</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface MultiSelectPopoverProps {
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (selected: string[]) => void;
}

function MultiSelectPopover({ label, options, selected, onChange }: MultiSelectPopoverProps) {
  const toggle = (value: string) => {
    onChange(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value]);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className={cn(
          "flex items-center gap-1.5 h-8 px-2.5 text-xs rounded-md border border-input bg-background hover:bg-accent/50 transition-colors",
          selected.length > 0 && "border-primary/40 bg-primary/5"
        )}>
          <span className="text-muted-foreground">{label}</span>
          {selected.length > 0 && (
            <Badge variant="secondary" className="h-4 px-1 text-[10px] font-medium rounded">{selected.length}</Badge>
          )}
          <ChevronDown className="w-3 h-3 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-40 p-1">
        <div className="space-y-0.5">
          {options.map(opt => (
            <label key={opt.value} className="flex items-center gap-2 px-2.5 py-1.5 rounded-sm hover:bg-accent cursor-pointer text-xs">
              <Checkbox
                checked={selected.includes(opt.value)}
                onCheckedChange={() => toggle(opt.value)}
                className="h-3.5 w-3.5"
              />
              {opt.label}
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function DateRangeFilter({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>(() => {
    if (value && value.includes(',')) {
      const [f, t] = value.split(',');
      return { from: f ? new Date(f) : undefined, to: t ? new Date(t) : undefined };
    }
    return {};
  });

  useEffect(() => {
    if (!value) {
      setDateRange({});
    } else if (value.includes(',')) {
      const [f, t] = value.split(',');
      setDateRange({ from: f ? new Date(f) : undefined, to: t ? new Date(t) : undefined });
    }
  }, [value]);

  const handleSelect = (range: any) => {
    if (range?.from && range?.to) {
      const from = new Date(range.from);
      from.setHours(0, 0, 0, 0);
      const to = new Date(range.to);
      to.setHours(23, 59, 59, 999);
      setDateRange({ from, to });
      onChange(`${format(from, 'yyyy-MM-dd')},${format(to, 'yyyy-MM-dd')}`);
    } else if (range?.from) {
      setDateRange({ from: range.from, to: undefined });
    }
  };

  const displayLabel = useMemo(() => {
    if (dateRange.from && dateRange.to) {
      return `${format(dateRange.from, 'MMM d, yyyy')} - ${format(dateRange.to, 'MMM d, yyyy')}`;
    }
    return 'Updated';
  }, [dateRange]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className={cn(
          "flex items-center gap-1.5 h-8 px-2.5 text-xs rounded-md border border-input bg-background hover:bg-accent/50 transition-colors",
          value && "border-primary/40 bg-primary/5"
        )}>
          <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-muted-foreground">{displayLabel}</span>
          <ChevronDown className="w-3 h-3 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <CalendarComponent
          initialFocus
          mode="range"
          defaultMonth={dateRange?.from || new Date()}
          selected={{ from: dateRange?.from, to: dateRange?.to }}
          onSelect={handleSelect}
          numberOfMonths={2}
        />
        {value && (
          <div className="px-3 pb-3 flex justify-end">
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setDateRange({}); onChange(''); }}>
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
